import { Patient } from '@/lib/types';
import { api, ApiResponse } from './api';

// Prescription Link interfaces
export interface PrescriptionLink {
  id: string;
  prescription_id: string;
  token: string;
  status: 'active' | 'used' | 'expired';
  expires_at: string;
  viewed_at?: string | null;
  created_at: string;
  updated_at: string;
}


export interface Practitioner {
  id: string;
  first_name: string;
  last_name: string;
  specialty?: string;
  npi?: string;
}

export interface Prescription {
  id: string;
  patient_id: string;
  practitioner_id: string;
  clinic_id: string;
  encounter_id?: string | null;
  appointment_id?: string | null;
  off_encounter_reason?: string | null;
  status: string;
  created_at: string;
  latest_prescription_link?: PrescriptionLink | null;
  // Shipping fields
  shipping_carrier_id?: string;
  shipping_carrier_name?: string;
  shipping_service_id?: string;
  shipping_service_name?: string;
  shipping_cost_cents?: number;
  shipping_original_cost_cents?: number;
  is_free_shipment?: boolean;
  free_shipment_type?: 'global' | 'clinic_specific' | null;
  shipping_metadata?: Record<string, any>;
  // Follow-up lab fields
  followup_days?: number | null;
  order_followup_lab?: boolean;
  followup_junction_test_id?: string | null;
  followupLabPanel?: {
    id: string;
    name: string;
    description?: string;
    treatment_area?: string;
    price?: number;
  };
  medicationRequests: Array<{
    id: string;
    medication: string;
    medication_name?: string;
    dose: string;
    sig: string;
    quantity_authorized: number;
    unit_of_measure: string;
    price?: number;
    selling_price_cents?: number;
    selling_price_dollars?: number;
    base_price_cents?: number;
    base_price_dollars?: number;
    product?: {
      id: string;
      description: string;
      ndc: string;
      rxcui: string;
      controlled_indicator: string;
      compound_indicator: string;
      dea_schedule_level: string;
      base_price_cents: number;
      base_price_dollars: number;
      clinic_price_cents: number;
      clinic_price_dollars: number;
      effective_price_cents: number;
      effective_price_dollars: number;
      created_at: string;
      updated_at: string;
      price_plan_cost?: number;
      price_per_unit?: number;
    };
    clinicProduct?: {
      id: string;
      selling_price_cents: number;
      base_price_cents: number;
      getSellingPriceInDollars?: () => number;
      getBasePriceInDollars?: () => number;
    };
  }>;
}

// Payload for creating a prescription
export interface CreatePrescriptionPayload {
  patientId: string;
  practitionerId: string;
  clinicId: string;
  // Encounter linkage — required by the server's clinicalContext middleware.
  // In-visit prescribing supplies encounter_id (+ optional appointment_id).
  // Off-visit flows (refills, Nimbus guided orders, external imports) supply
  // off_encounter_reason instead.
  encounter_id?: string;
  appointment_id?: string;
  off_encounter_reason?:
    | 'refill_request'
    | 'chart_correction'
    | 'letter'
    | 'external_import'
    | 'pre_visit_task'
    | 'external_order'
    | 'pre_visit_lab'
    | 'direct_rx'
    | 'patient_storefront';
  medicationRequests: Array<{
    medication: string;
    dose: string;
    refills?: string;
    quantity: string;
    unit: string;
    sig: string;
    reason: string;
    additionalContext?: string;
    patientContactRequested?: boolean;
    doNotRefill?: boolean;
    refillNeeded?: boolean;
    doNotFill?: boolean;
    program?: string;
    program_id?: string | null;
    clinic_program_id?: string | null;
    daysSupply?: string;
    flavor?: string;
    notes?: string;
    product_id?: string | null;
    template_id?: string | null;
    discount_cents?: number;
  }>;
  priority?: number;
  treatment_type?: string;
  // Follow-up lab fields
  followup_days?: number | null;
  order_followup_lab?: boolean;
  followup_junction_test_id?: string | null;
  // Allergy acknowledgement
  allergyWarningsAcknowledged?: boolean;
}

export interface PrescriptionLinkResponse {
  prescriptionLink: PrescriptionLink;
  prescription: Prescription;
  patient: Patient;
  practitioner?: Practitioner;
}

export const prescriptionService = {
  savePrescription: (payload: CreatePrescriptionPayload): Promise<ApiResponse<{[key: string]: any}>> => {
    // The backend's requireClinicalContext middleware enforces that every
    // clinical write carries an encounter_id OR an off_encounter_reason.
    // Current Rx entry points are all out-of-visit; default to direct_rx
    // so callers can stay concise. The Start Visit workflow (Phase 2) will
    // supply encounter_id instead and this default becomes a no-op.
    const enriched: CreatePrescriptionPayload = {
      ...payload,
      off_encounter_reason:
        payload.off_encounter_reason
        || (payload.encounter_id ? undefined : 'direct_rx'),
    };
    return api.post<ApiResponse<{[key: string]: any}>>(
      'clinic/patients/save-medication-request',
      enriched,
    );
  },

  getPrescriptionsByPatientId: (patientId: string, params: any): Promise<ApiResponse<any>> => {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.search) queryParams.append('search', params.search);
    if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params?.sortOrder) queryParams.append('sortOrder', params.sortOrder);
    return api.get<ApiResponse<any>>(
      `clinic/patients/${patientId}/prescriptions?${queryParams.toString()}`
    );
  },

  getPrescriptionByClinicId: (clinicId: string, params: any): Promise<ApiResponse<any>> => {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.search) queryParams.append('search', params.search);
    if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params?.sortOrder) queryParams.append('sortOrder', params.sortOrder);
    if (params?.status) queryParams.append('status', params.status);
    if (params?.payment_status) queryParams.append('payment_status', params.payment_status);
    if (params?.practitionerId) queryParams.append('practitionerId', params.practitionerId);
    return api.get<ApiResponse<any>>(`clinic/${clinicId}/prescriptions?${queryParams.toString()}`);
  },

  getPrescriptionByPractitionerID: (practitionerId: string, params: any): Promise<ApiResponse<any>> => {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.search) queryParams.append('search', params.search);
    if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params?.sortOrder) queryParams.append('sortOrder', params.sortOrder);
    if (params?.status) queryParams.append('status', params.status);
    if (params?.payment_status) queryParams.append('payment_status', params.payment_status);
    return api.get<ApiResponse<any>>(`practitioners/${practitionerId}/prescriptions?${queryParams.toString()}`);
  },

  verifyPrescription: (prescriptionId: string): Promise<ApiResponse<any>> => {
    return api.post<ApiResponse<any>>(`clinic/prescriptions/${prescriptionId}/verify`);
  },

  submitPrescription: async (prescriptionId: string): Promise<ApiResponse<any> & { isExternalFulfillment?: boolean }> => {
    const result = await api.post<ApiResponse<any>>(`clinic/prescriptions/${prescriptionId}/submit`);
    const data = (result as any)?.data ?? result;
    if (data?.routed === 'external_fulfillment') {
      return { ...result, isExternalFulfillment: true } as any;
    }
    return result;
  },

  getPrescriptionById: (clinicId: string, prescriptionId: string): Promise<ApiResponse<any>> => {
    return api.get<ApiResponse<any>>(`clinic/${clinicId}/prescriptions/${prescriptionId}`);
  },

  sendPrescriptionLink: (prescriptionId: string, email?: string): Promise<ApiResponse<any>> => {
    return api.post<ApiResponse<any>>(`clinic/prescriptions/${prescriptionId}/send-link`, email ? { email } : undefined);
  },

  getPrescriptionLink: (token: string): Promise<ApiResponse<PrescriptionLinkResponse>> => {
    return api.get<ApiResponse<PrescriptionLinkResponse>>(`clinic/storefront/link/${token}`);
  },

  markPrescriptionLinkAsUsed: (prescriptionLinkId: string): Promise<ApiResponse<any>> => {
    return api.patch<ApiResponse<any>>(`clinic/prescriptions/link/${prescriptionLinkId}/mark-used`);
  },

  markPrescriptionLinkAsViewed: (prescriptionLinkId: string): Promise<ApiResponse<any>> => {
    return api.patch<ApiResponse<any>>(
      `clinic/prescriptions/link/${prescriptionLinkId}/mark-viewed`,
    );
  },

  updatePrescriptionLinkAddress: (
    token: string,
    address: {
      line_1: string;
      line_2?: string;
      city: string;
      state_code: string;
      postal_code: string;
    },
  ): Promise<ApiResponse<any>> => {
    return api.patch<ApiResponse<any>>(`clinic/storefront/link/${token}/address`, address);
  },

  getPrescriptionAuditTrail: (prescriptionId: string): Promise<ApiResponse<any>> => {
    return api.get<ApiResponse<any>>(`clinic/prescriptions/${prescriptionId}/audit-trail`);
  },

  getPendingPrescriptionCount: (clinicId: string): Promise<ApiResponse<{ count: number }>> => {
    return api.get<ApiResponse<{ count: number }>>(`clinic/${clinicId}/prescriptions/pending/count`);
  },

  getStatusCounts: (clinicId: string): Promise<ApiResponse<{ counts: Record<string, number> }>> => {
    return api.get<ApiResponse<{ counts: Record<string, number> }>>(`clinic/${clinicId}/prescriptions/status-counts`);
  },

  deleteMedicationRequest: async (
    prescriptionId: string,
    medicationRequestId: string
  ): Promise<ApiResponse<any>> => {
    return api.delete(
      `clinic/prescriptions/${prescriptionId}/medications/${medicationRequestId}`
    );
  },

  updatePrescription: async (
    prescriptionId: string,
    payload: {
      priority?: number;
      medicationRequests?: Array<{
        id?: string;
        medication?: string;
        dose?: string;
        quantity_authorized?: string;
        unit_of_measure?: string;
        refills_authorized?: string;
        sig?: string;
        day_supply?: string;
        flavor?: string;
        notes?: string;
        product_id?: string | null;
        clinic_program_id?: string | null;
      }>;
    }
  ): Promise<ApiResponse<any>> => {
    return api.patch<ApiResponse<any>>(
      `clinic/prescriptions/${prescriptionId}`,
      payload
    );
  },
};
